import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useState } from 'react'

import { getFPEmailSchema, getFPAuthenticationSchema, getFPPasswordSchema } from '@/@data/users'

import type { IResetPasswordPayload, RegisterPayload } from '@/@data/users'
import { useTranslator } from './useTranslator'
import {
  IHttpError,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyForgotPasswordMutation
} from '@/@data'
import { useNotification, useRouteQuery } from '.'

const defaultValues: Partial<IResetPasswordPayload> = {
  email: '',
  password: '',
  confirm_password: '',
  token: ''
}

export const useForgotPasswordStepForm = (callBack: () => void) => {
  const [activeStep, setActiveStep] = useState(0)
  const [loadingForm, setLoadingForm] = useState(false)
  const [serverError, setServerError] = useState<IHttpError | null>(null)

  const forgotPasswordMutation = useForgotPasswordMutation()
  const verifyForgotPassword = useVerifyForgotPasswordMutation()
  const resetPasswordMutation = useResetPasswordMutation()
  const n = useNotification()

  const { __ } = useTranslator()
  const { navigate } = useRouteQuery()

  const getStepSchema = (step: number): yup.ObjectSchema<any> => {
    switch (step) {
      case 0:
        return getFPEmailSchema(__)
      case 1:
        return getFPAuthenticationSchema(__)
      case 2:
        return getFPPasswordSchema(__)
      default:
        return yup.object()
    }
  }

  const methods = useForm<IResetPasswordPayload>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(getStepSchema(activeStep))
  })

  const handleNext = async () => {
    setLoadingForm(true)
    setServerError(null)
    const isValid = await methods.trigger()
    const payload = methods.control._formValues as IResetPasswordPayload

    if (isValid) {
      if (activeStep === 0) {
        await getVerificationCode()
      }

      if (activeStep === 1) {
        await verifyForgotPassword.mutateAsync(payload, {
          onSuccess({ data }) {
            methods.setValue('token', data.token)
            setActiveStep(prev => prev + 1)
          },
          onError: () => {
            setLoadingForm(false)
            methods.setError('verification_code', { message: __('OTP Code invalid') })
          }
        })
      }
    }

    setLoadingForm(false)
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
  }

  const handleSubmit = async (data: IResetPasswordPayload) => {
    await resetPasswordMutation.mutateAsync(data, {
      onSuccess: () => {
        methods.reset()
        n.success(__('Password reset successfully'))
        navigate('/cms/login')
        // setActiveStep(prev => prev + 1)
        callBack?.()
      },
      onError: error => {
        setServerError(error)
      }
    })
  }

  async function getVerificationCode(handleStep = true) {
    const payload = methods.control._formValues as RegisterPayload

    await forgotPasswordMutation.mutateAsync(payload, {
      onSuccess: () => {
        if (handleStep) {
          setActiveStep(prev => prev + 1)
        }
      },
      onError: () => {
        methods.setError('email', { message: __('We couldn’t find an account matching the email you entered. Please check your email try again.') })
        setLoadingForm(false)
      }
    })
  }

  return {
    methods,
    activeStep,
    setActiveStep,
    loadingForm,
    serverError,
    setServerError,
    getVerificationCode,
    setLoadingForm,
    handleNext,
    handleBack,
    handleSubmit: methods.handleSubmit(handleSubmit),
    isStepValid: methods.formState.isValid
  }
}
